import { useEffect, useRef } from 'react';
// mapboxgl is available; types referenced via generics
import { updateLayerTheme } from '../layers';
import type { MarketZoneId } from '@app/common/static-data/energy-markets';

interface UseMapSelectionEffectsProps {
  mapRef: React.MutableRefObject<mapboxgl.Map | null>;
  zoneFeatureMapRef: React.MutableRefObject<Map<string, number[]>>;
  zoneCentersRef: React.MutableRefObject<Map<string, [number, number]>>;
  // selectedZone kept for compatibility (unused)
  selectedZone: MarketZoneId | null;
  selectedZones: Set<string>;
  zonesLoaded: boolean;
  theme: string;
  activeTab: 'list' | 'map';
  centerOnZone: (zoneId: string, animate?: boolean) => void;
  handleClearSelections: () => void;
}

export const useMapSelectionEffects = ({
  mapRef,
  zoneFeatureMapRef,
  zoneCentersRef,
  selectedZones,
  zonesLoaded,
  theme,
  activeTab,
  centerOnZone,
  handleClearSelections,
}: UseMapSelectionEffectsProps) => {
  // Update zone colors when theme changes

  useEffect(() => {
    if (mapRef.current && zonesLoaded && mapRef.current.isStyleLoaded()) {
      try {
        updateLayerTheme(mapRef.current, theme);
      } catch (error) {
        console.warn('Map style not ready for theme updates:', error);
      }
    }
  }, [mapRef, theme, zonesLoaded]);

  // Track previous selection to apply diffs only and recover selection after reload
  const prevSelectedRef = useRef<Set<string>>(new Set());

  // Update map visual state when global selectedZones change
  useEffect(() => {
    if (!mapRef.current || !zonesLoaded) return;

    const map = mapRef.current;

    // Check if map style is loaded before setting feature states
    if (!map.isStyleLoaded()) {
      // Wait for style to load and then retry
      const onStyleData = () => {
        if (map.isStyleLoaded()) {
          map.off('styledata', onStyleData);
          // Retry the effect after style is loaded
          setTimeout(() => {
            // Re-run this effect logic after style is ready
            updateMapSelectionState();
          }, 100);
        }
      };
      map.on('styledata', onStyleData);
      return;
    }

    updateMapSelectionState();

    function updateMapSelectionState() {
      if (!mapRef.current || !zonesLoaded || !mapRef.current.isStyleLoaded()) return;
      try {
        const prev = prevSelectedRef.current;
        const next = selectedZones;

        // Compute diffs
        const toAdd: string[] = [];
        next.forEach((z) => {
          if (!prev.has(z)) toAdd.push(z);
        });
        const toRemove: string[] = [];
        prev.forEach((z) => {
          if (!next.has(z)) toRemove.push(z);
        });
        // Apply adds (chunked)
        const applyFeatureStateInChunks = (
          featureIds: number[] | undefined,
          selected: boolean,
          chunkSize: number = 500,
        ) => {
          if (!mapRef.current || !featureIds || featureIds.length === 0) return;
          let index = 0;
          const step = () => {
            const end = Math.min(index + chunkSize, featureIds.length);
            for (let i = index; i < end; i++) {
              const id = featureIds[i];
              mapRef.current?.setFeatureState({ source: 'tso-zones', id }, { selected });
            }
            index = end;
            if (index < featureIds.length) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        };

        toAdd.forEach((zoneId) => {
          const featureIds = zoneFeatureMapRef.current.get(zoneId);
          applyFeatureStateInChunks(featureIds, true);
        });

        // Apply removals (chunked)
        toRemove.forEach((zoneId) => {
          const featureIds = zoneFeatureMapRef.current.get(zoneId);
          applyFeatureStateInChunks(featureIds, false);
        });

        // Update previous selection
        prevSelectedRef.current = new Set(next);
      } catch (error) {
        console.warn('Map style not ready for feature state updates:', error);
      }
    }
    // After selection state applied, fit map to include all selected zones (if any)
    try {
      if (activeTab === 'map' && selectedZones && selectedZones.size > 0) {
        const coords: [number, number][] = [];
        selectedZones.forEach((zoneId) => {
          const c = zoneCentersRef.current.get(zoneId);
          if (c) coords.push(c);
        });
        if (coords.length === 1) {
          centerOnZone(Array.from(selectedZones)[0], false);
        } else if (coords.length > 1) {
          let minLng = Infinity,
            minLat = Infinity,
            maxLng = -Infinity,
            maxLat = -Infinity;
          coords.forEach(([lng, lat]) => {
            if (lng < minLng) minLng = lng;
            if (lat < minLat) minLat = lat;
            if (lng > maxLng) maxLng = lng;
            if (lat > maxLat) maxLat = lat;
          });
          if (isFinite(minLng) && isFinite(minLat) && isFinite(maxLng) && isFinite(maxLat)) {
            const degPad = 0.25;
            map.fitBounds(
              [
                [minLng - degPad, minLat - degPad],
                [maxLng + degPad, maxLat + degPad],
              ],
              { padding: 60, duration: 700, essential: true, maxZoom: 4.2 },
            );
          }
        }
      }
    } catch {
      // ignore fit errors
    }
  }, [
    mapRef,
    zoneFeatureMapRef,
    zoneCentersRef,
    selectedZones,
    zonesLoaded,
    activeTab,
    centerOnZone,
  ]);

  // Handle double-click to clear selections
  useEffect(() => {
    if (mapRef.current && zonesLoaded) {
      const map = mapRef.current;

      const handleDoubleClick = () => {
        if (selectedZones.size > 0) {
          handleClearSelections();
        }
      };

      map.on('dblclick', handleDoubleClick);

      return () => {
        map.off('dblclick', handleDoubleClick);
      };
    }
  }, [mapRef, selectedZones.size, zonesLoaded, handleClearSelections]);
};
